// components/team/TransferOwnershipModal.tsx
import React, { useState, useEffect } from 'react';
import { FiX } from 'react-icons/fi';
import { toast, ToastContainer } from 'react-toastify';
import { getAuthContext, decodeAuthToken } from '../../utils/jwt';
import { APIMember } from '../../features/members/membersSlice';
import { BASE_URL } from '../../utils/constants';

interface TransferOwnershipModalProps {
  isOpen: boolean;
  onClose: () => void;
  setOwnerRole: any;
  currentUserEmail?: string;
}

const FiXComponent = FiX as unknown as React.ComponentType<{
  size?: number | string;
  className?: string;
}>;

const TransferOwnershipModal: React.FC<TransferOwnershipModalProps> = ({
  isOpen,
  onClose,
  setOwnerRole,
  currentUserEmail,
}) => {
  const [selectedUserId, setSelectedUserId] = useState<string>('');
  const [confirmChecked, setConfirmChecked] = useState(false);
  const [members, setMembers] = useState<APIMember[]>([]);
  const [loading, setLoading] = useState(false);

  // Fetch members when modal opens
  useEffect(() => {
    if (isOpen) {
      fetchMembers();
    } else {
      // Reset state when modal closes
      setSelectedUserId('');
      setConfirmChecked(false);
    }
  }, [isOpen]);

  // Get auth email as fallback
  const getAuthEmail = (): string => {
    try {
      const decoded = decodeAuthToken();
      return decoded?.email || '';
    } catch (error) {
      // console.error('Failed to get email from auth:', error);
      toast.error('Failed to get email from auth');
      return '';
    }
  };

  const fetchMembers = async () => {
    try {
      setLoading(true);
      const { token, tenantId, userId } = getAuthContext();

      if (!token || !tenantId) {
        toast.error('Authentication required');
        return;
      }

      const response = await fetch(`${BASE_URL}/v1/team/members`, {
        method: 'GET',
        headers: {
          'X-Tenant-Id': tenantId,
          'X-User-Id': userId || '',
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch team members: ${response.status}`);
      }

      const data = await response.json();
      const allItems = data.items || [];

      // Get the current user's email
      const userEmail = currentUserEmail || getAuthEmail();

      // Filter only members with roleName === 'Admin' and rowType === 'MEMBER'
      // Also filter out the current user
      const adminMembers = allItems.filter(
        (item: APIMember) =>
          item.rowType === 'MEMBER' &&
          item.roleName === 'Admin' &&
          item.status === 'ACTIVE' &&
          item.email !== userEmail
      );

      setMembers(adminMembers);
    } catch (error: any) {
      toast.error(error.message || 'Failed to fetch members');
      // console.error('Fetch members error:', error);
    } finally {
      setLoading(false);
    }
  };

  const isOwnerSelected = selectedUserId !== '';

  // Get the selected member's display name for the info box
  // const getSelectedMemberName = (): string => {
  //   const member = members.find((m) => m.userId === selectedUserId);
  //   return member ? (member.displayName || member.email) : '';
  // };

  // Handles confirmation of ownership transfer, updates role, and shows toast
  const handleConfirm = async () => {
    if (!selectedUserId) {
      toast.error('Please select a member');
      return;
    }

    try {
      const { token, tenantId, userId } = getAuthContext();

      if (!token || !tenantId) {
        toast.error('Authentication required');
        return;
      }

      const loadingToast = toast.loading('Transferring ownership...');

      // Make API call to transfer ownership with correct body
      const response = await fetch(`${BASE_URL}/v1/team/ownership/transfer`, {
        method: 'POST',
        headers: {
          'X-Tenant-Id': tenantId,
          'X-User-Id': userId || '',
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          newOwnerUserId: selectedUserId,
          acknowledged: confirmChecked,
        }),
      });

      toast.dismiss(loadingToast);

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `Failed to transfer ownership: ${response.status}`);
      }

      // Update the owner role in parent component
      if (setOwnerRole) {
        setOwnerRole('Admin');
      }

      toast.success('Ownership transferred successfully!');
      onClose();

      // Reload the page to refresh all data
      window.location.reload();

    } catch (error: any) {
      toast.error(error.message || 'Failed to transfer ownership');
      // console.error('Transfer ownership error:', error);
    }
  };

  if (!isOpen) return null;

  return (
    <div
      className='fixed inset-0 flex items-center justify-center bg-black bg-opacity-40 z-50'
      style={{
        marginTop: '0px',
      }}
    >
      <div className='bg-white rounded-lg shadow-lg w-[550px] p-6 relative'>
        {/* Close Button */}
        <button
          onClick={onClose}
          className='absolute top-4 right-4 text-gray-500 hover:text-gray-700'
        >
          <FiXComponent size={20} />
        </button>

        {/* Title */}
        <h2 className='text-[20px] font-semibold mb-1 text-[#3F5265]'>
          Transfer Ownership
        </h2>
        <p className='text-[#657584] text-[16px] mb-4'>
          Easily transfer ownership to the right admin
        </p>

        {/* Dropdown */}
        <label className='block text-[12px] mb-2 text-[#9FA9B2]'>
          CHOOSE OWNER <span className='text-[red]'>*</span>
        </label>
        <select
          value={selectedUserId}
          onChange={(e) => {
            setSelectedUserId(e.target.value);
          }}
          className='w-full border rounded-md px-3 py-3 focus:outline-none focus:ring-2 focus:ring-[#00A4BD] bg-[#F5F8FA] text-[#3F5265]'
          disabled={loading}
        >
          <option value=''>
            {loading ? 'Loading admins...' : 'Choose and search Admin'}
          </option>
          {members.map((member) => (
            <option key={member.memberId} value={member.userId}>
              {member.displayName || member.email}
            </option>
          ))}
        </select>

        {/* Show message when no admins found */}
        {!loading && members.length === 0 && (
          <p className='text-sm text-yellow-600 mt-2'>
            No other admin members found. Please add an admin first.
          </p>
        )}

        {/* Info Box */}
        <div
          className={`mt-4 p-3 text-sm rounded-md ${isOwnerSelected
            ? 'bg-[#E0F8FB] text-[#00899E]'
            : 'bg-[#FCECEC] text-[#C43030]'
            }`}
        >
          <p>Once Transferred:</p>
          <ul className='list-disc list-inside space-y-1'>
            <li>You will lose owner privileges.</li>
            <li>
              The new owner will have full control of account settings, billing,
              and user management.
            </li>
            <li>This action cannot be undone.</li>
          </ul>
        </div>

        {/* Action Buttons */}
        <div className='flex justify-between mt-10 items-center'>
          <div className='flex gap-2'>
            <input
              type='checkbox'
              checked={confirmChecked}
              onChange={(e) => setConfirmChecked(e.target.checked)}
              className='w-4 h-4'
            />
            <span className='text-[12px] text-[#3F5265]'>
              I understand I will lose all ownership rights.
            </span>
          </div>
          <div className='flex justify-between gap-3'>
            <button
              onClick={onClose}
              className='px-4 py-2 rounded-md border border-red-400 text-red-500 hover:bg-red-50'
            >
              Cancel
            </button>
            <button
              disabled={!isOwnerSelected || !confirmChecked || loading}
              className={`px-4 py-2 rounded-md ${!isOwnerSelected || !confirmChecked || loading
                ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                : 'bg-red-500 text-white'
                }`}
              onClick={handleConfirm}
            >
              {loading ? 'Loading...' : 'Confirm'}
            </button>
          </div>
        </div>
      </div>
      <ToastContainer position='top-right' autoClose={3000} />
    </div>
  );
};

export default TransferOwnershipModal;